import { useState, useEffect } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import LocationLookup from '../components/seotools/LocationLookup';
import SERPRankChecker from '../components/seotools/SERPRankChecker';
import GMBAudit from '../components/seotools/GMBAudit';
import ContentAudit from '../components/seotools/ContentAudit';
import ContentFreshness from '../components/seotools/ContentFreshness';
import ContentBriefGenerator from '../components/seotools/ContentBriefGenerator';
import SiteAudit from '../components/seotools/SiteAudit';
import KeywordCannibalism from '../components/seotools/KeywordCannibalism';
import GoogleAnalytics from '../components/seotools/GoogleAnalytics';
import GoogleAdsAudit from '../components/seotools/GoogleAdsAudit';
import SFSiteAudit from '../components/seotools/SFSiteAudit';

const TABS = [
  { id: 'serp-rank', label: 'SERP Rank', phase: 1 },
  { id: 'gmb-audit', label: 'GMB Audit', phase: 1 },
  { id: 'content-brief', label: 'Content Brief', phase: 3 },
  { id: 'content-audit', label: 'Content Audit', phase: 2 },
  { id: 'content-freshness', label: 'Content Freshness', phase: 2 },
  { id: 'site-audit', label: 'Site Audit', phase: 4 },
  { id: 'sf-site-audit', label: 'SF Site Audit', phase: 6 },
  { id: 'keyword-cannibalism', label: 'Keyword Cannibalism', phase: 5 },
  { id: 'google-analytics', label: 'Google Analytics', phase: 5 },
  { id: 'google-ads', label: 'Google Ads Audit', phase: 5 },
  { id: 'location-lookup', label: 'Location Lookup', phase: 1 },
] as const;

type TabId = typeof TABS[number]['id'];

export default function HolisticSEOPage() {
  const { projectId: urlProjectId } = useParams<{ projectId: string }>();
  const [searchParams] = useSearchParams();
  // Support both new URL structure (/projects/:projectId/agent) and legacy (?project_id=...)
  const projectId = urlProjectId || searchParams.get('project_id') ? Number(urlProjectId || searchParams.get('project_id')) : null;

  const [activeTab, setActiveTab] = useState<TabId>('serp-rank');
  const [apiStatus, setApiStatus] = useState<{ dataforseo: boolean; googleOAuth: boolean } | null>(null);

  // Read hash on mount for deep linking
  useEffect(() => {
    const hash = window.location.hash.replace('#', '') as TabId;
    if (TABS.some(t => t.id === hash)) {
      setActiveTab(hash);
    }
  }, []);

  // Update hash when tab changes
  useEffect(() => {
    window.location.hash = activeTab;
  }, [activeTab]);

  // Check API status
  useEffect(() => {
    fetch('/api/seotools/status')
      .then(r => r.json())
      .then(setApiStatus)
      .catch(() => setApiStatus({ dataforseo: false, googleOAuth: false }));
  }, []);

  const renderTab = () => {
    switch (activeTab) {
      case 'serp-rank': return <SERPRankChecker />;
      case 'gmb-audit': return <GMBAudit />;
      case 'content-brief': return <ContentBriefGenerator />;
      case 'content-audit': return <ContentAudit />;
      case 'content-freshness': return <ContentFreshness />;
      case 'site-audit': return <SiteAudit />;
      case 'sf-site-audit': return <SFSiteAudit />;
      case 'keyword-cannibalism': return <KeywordCannibalism />;
      case 'google-analytics': return <GoogleAnalytics />;
      case 'google-ads': return <GoogleAdsAudit />;
      case 'location-lookup': return <LocationLookup />;
      default: return <SERPRankChecker />;
    }
  };

  return (
    <Layout projectId={projectId}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Holistic SEO Tools</h1>
          <p className="text-sm text-gray-500 mt-1">
            12 SEO analysis tools — SERP rankings, GMB audit, content analysis, site audit, and more
          </p>
          {apiStatus && !apiStatus.dataforseo && (
            <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
              DataForSEO credentials not configured. Add <code className="bg-amber-100 px-1 rounded">DATAFORSEO_LOGIN</code> and <code className="bg-amber-100 px-1 rounded">DATAFORSEO_PASSWORD</code> to your .env file. CSV-based tools (Content Audit, Content Freshness) work without API keys.
            </div>
          )}
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex gap-1 overflow-x-auto">
            {TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap py-3 px-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        <div>{renderTab()}</div>
      </div>
    </Layout>
  );
}
